<template>
  <div class="por-price-drop-banner">
    <img
      class="por-price-drop-banner__icon"
      data-qa="por-price-drop-banner-icon"
      :src="priceDropIcon"
      alt=""
      width="44"
      height="44">
    <div class="por-price-drop-banner__content">
      <div class="por-price-drop-banner__heading-wrapper">
        <p
          class="por-price-drop-banner__heading"
          data-qa="por-price-drop-banner-heading">
          {{ $t('priceDropBanner_heading_text') }}
        </p>
        <b-tooltip
          ref="tooltip"
          class="por-price-drop-banner__tooltip"
          position="is-bottom"
          always
          :active="isTooltipOpen"
          :triggers="[]">
          <button
            type="button"
            class="por-price-drop-banner__info-button"
            data-qa="por-price-drop-banner-info-button"
            :aria-label="$t('priceDropBanner_info_button')"
            :aria-expanded="isTooltipOpen ? 'true' : 'false'"
            @click="toggleTooltip">
            <img
              class="por-price-drop-banner__info-icon"
              data-qa="por-price-drop-banner-info-icon"
              :src="infoIcon"
              alt=""
              width="20"
              height="20">
          </button>
          <template #content>
            <div
              class="por-price-drop-banner__tooltip-inner"
              data-qa="por-price-drop-banner-tooltip">
              <p class="por-price-drop-banner__tooltip-title">{{ $t('priceDropBanner_tooltipTitle_text') }}</p>
              <p>
                {{ $t('priceDropBanner_tooltipBody_text') }}
                <a
                  class="por-price-drop-banner__tooltip-terms"
                  data-qa="por-price-drop-banner-tooltip-terms"
                  :href="priceMatchTermsUrl"
                  target="_blank"
                  rel="noopener noreferrer">{{ $t('priceDropBanner_tooltipTerms_text') }}</a>
              </p>
            </div>
          </template>
        </b-tooltip>
      </div>
      <p
        class="por-price-drop-banner__subheading"
        data-qa="por-price-drop-banner-subheading">
        {{ $t('priceDropBanner_subheading_text') }}
      </p>
    </div>
  </div>
</template>

<script>
import infoIcon from '@/assets/icons/info.svg'
import priceDropIcon from '@/assets/icons/price-drop.svg'

const priceMatchTermsUrl = 'https://www.zavamed.com/uk/zava-price-match-index.html'

export default {
  name: 'PriceDropBanner',
  data () {
    return {
      infoIcon,
      priceDropIcon,
      priceMatchTermsUrl,
      isTooltipOpen: false
    }
  },
  methods: {
    toggleTooltip () {
      this.isTooltipOpen = !this.isTooltipOpen
    },
    closeTooltip () {
      this.isTooltipOpen = false
    },
    // Buefy's own click trigger cannot toggle, so the tooltip is controlled here
    // and closed when the user clicks outside of it or presses Escape
    onDocumentClick (event) {
      if (this.isTooltipOpen && !this.$refs.tooltip.$el.contains(event.target)) {
        this.closeTooltip()
      }
    },
    onDocumentKeydown ({ key }) {
      if (key === 'Escape' || key === 'Esc') {
        this.closeTooltip()
      }
    }
  },
  mounted () {
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onDocumentKeydown)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onDocumentKeydown)
  }
}
</script>

<style lang="scss">
  $por-price-drop-banner-background: #BAEE72;
  $por-price-drop-banner-margin-top: 4px;
  $por-price-drop-banner-margin-top-narrow: 2px;
  $por-price-drop-banner-padding: 16px;
  $por-price-drop-banner-gap: 16px;
  $por-price-drop-banner-content-gap: 4px;
  $por-price-drop-banner-heading-gap: 4px;
  $por-price-drop-banner-info-icon-margin-left: 6px;
  $por-price-drop-banner-line-height: 140%;
  $por-price-drop-banner-tooltip-background: #2B2E33;
  $por-price-drop-banner-tooltip-padding: 16px;
  $por-price-drop-banner-tooltip-radius: 8px;
  $por-price-drop-banner-tooltip-width: 326px;
  $por-price-drop-banner-tooltip-gap: 8px;
  $por-price-drop-banner-tooltip-line-height: 21px;
  $por-price-drop-banner-tooltip-shadow: 0 24px 7px 0 rgba(0, 0, 0, 0), 0 15px 6px 0 rgba(0, 0, 0, 0.04), 0 9px 5px 0 rgba(0, 0, 0, 0.13), 0 4px 4px 0 rgba(0, 0, 0, 0.21), 0 1px 2px 0 rgba(0, 0, 0, 0.25);

  .por-price-drop-banner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $por-price-drop-banner-gap;
    width: 100%;
    margin-top: $por-price-drop-banner-margin-top; // To compensate for header border
    padding: $por-price-drop-banner-padding;
    background: $por-price-drop-banner-background;
    color: $c-300;
  }

  .por-price-drop-banner__icon,
  .por-price-drop-banner__info-icon {
    display: block;
    flex-shrink: 0;
  }

  .por-price-drop-banner__info-icon {
    margin-left: $por-price-drop-banner-info-icon-margin-left;
  }

  .por-price-drop-banner__content {
    display: flex;
    flex-direction: column;
    gap: $por-price-drop-banner-content-gap;
  }

  .por-price-drop-banner__heading-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: $por-price-drop-banner-heading-gap;
  }

  .por-price-drop-banner__heading {
    font-size: $font-size-7;
    font-weight: 700;
    line-height: $por-price-drop-banner-line-height;
    letter-spacing: 0.16px;
  }

  .por-price-drop-banner__subheading {
    width: fit-content;
    font-size: $font-size-8;
    font-weight: 600;
    line-height: $por-price-drop-banner-line-height;
  }

  .por-price-drop-banner__info-button {
    display: flex;
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
  }

  // Scoped under the banner root so these out-rank Buefy's .b-tooltip.is-primary rules
  .por-price-drop-banner .b-tooltip.por-price-drop-banner__tooltip {
    .tooltip-content {
      width: $por-price-drop-banner-tooltip-width;
      padding: $por-price-drop-banner-tooltip-padding;
      border-radius: $por-price-drop-banner-tooltip-radius;
      background: $por-price-drop-banner-tooltip-background;
      box-shadow: $por-price-drop-banner-tooltip-shadow;
      color: $white;
      font-size: $font-size-8;
      font-weight: 400;
      line-height: $por-price-drop-banner-tooltip-line-height;
      text-align: left;
      white-space: normal;
    }

    .tooltip-content::before {
      display: none;
    }
  }

  .por-price-drop-banner__tooltip-inner {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $por-price-drop-banner-tooltip-gap;
  }

  .por-price-drop-banner__tooltip-title {
    font-weight: 700;
  }

  .por-price-drop-banner__tooltip-terms {
    color: inherit;
    text-decoration: underline;

    &:hover,
    &:focus {
      color: inherit;
    }
  }

  @media(max-width: 1199px) {
    .por-price-drop-banner {
      margin-top: $por-price-drop-banner-margin-top-narrow; // Header border is thinner below 1200px
    }
  }

  @media(max-width: 768px) {
    .por-price-drop-banner__info-icon {
      margin-left: 0;
    }

    .por-price-drop-banner__content {
      flex: 1 1 0;
    }

    .por-price-drop-banner__heading-wrapper {
      justify-content: space-between;
      gap: 0;
    }

    .por-price-drop-banner__subheading {
      width: auto;
    }

    .por-price-drop-banner .b-tooltip.por-price-drop-banner__tooltip.is-bottom .tooltip-content {
      right: 0;
      left: auto;
      transform: none;
    }
  }
</style>
